import { BadgeCardRow } from '@bp/ui-components';
import dayjs from 'dayjs';
import type { LessonItemFragment } from '../../types/planung-graphql-client-defs';
import { createIntArray } from '../../utils/arrayFunc';
import { getDaysDifference } from '../../utils/dateCalculations';
import type { TeachingBlockVersionDataCard } from '../TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import type { TeachingBlockCardType } from './types';

type GridCard = { lessonUuid: string; teachingBlockCardUuid: string };
type ClassGridCard = TeachingBlockCardType['classGridCards'][0] &
  GridCard & { divisionsCount?: number | null; usedPosition?: number | null; colors?: string[] | null };

type BoardCardSources<
  Subject extends NonNullable<TeachingBlockCardType['subject']>,
  Class extends TeachingBlockCardType['classes'][0] & { name: string },
  Person extends TeachingBlockCardType['teachers'][0],
  Group extends TeachingBlockCardType['groups'][0],
> = {
  cards: TeachingBlockVersionDataCard[];
  lessons: LessonItemFragment[];
  gridCards: {
    classGridCards: ClassGridCard[];
    teacherGridCards: Array<TeachingBlockCardType['teacherGridCards'][0] & GridCard>;
    roomGridCards: Array<TeachingBlockCardType['roomGridCards'][0] & GridCard>;
  };
  subjects: Subject[];
  classes: Class[];
  people: Person[];
  groups: Group[];
};

const groupBy = <T>(items: T[], key: (item: T) => string): Map<string, T[]> => {
  const grouped = new Map<string, T[]>();
  items.forEach((item) => {
    const list = grouped.get(key(item));
    if (list) list.push(item);
    else grouped.set(key(item), [item]);
  });
  return grouped;
};

/**
 * Adapter zwischen dem uuid-basierten Lesemodell der Version und den Karten-Objekten, mit denen die
 * Stores arbeiten. Eine Karte zeigt auf ihre Zuordnung; die Zuordnung liefert Fach, Klassen,
 * Gruppen, Lehrkräfte und Raumvorrat, die Stammdaten liefern die Objekte dazu.
 *
 * Wer die Stores später auf uuid-Auflösung umstellt, ersetzt nur diese Funktion.
 */
export function buildBoardCards<
  Subject extends NonNullable<TeachingBlockCardType['subject']>,
  Class extends TeachingBlockCardType['classes'][0] & { name: string },
  Person extends TeachingBlockCardType['teachers'][0],
  Group extends TeachingBlockCardType['groups'][0],
>({
  cards,
  lessons,
  gridCards,
  subjects,
  classes,
  people,
  groups,
}: BoardCardSources<Subject, Class, Person, Group>): TeachingBlockCardType[] {
  const lessonByUuid = new Map(lessons.map((lesson) => [lesson.uuid, lesson]));
  const subjectByUuid = new Map(subjects.map((subject) => [subject.uuid, subject]));
  const classByUuid = new Map(classes.map((c) => [c.uuid, c]));
  const personByUuid = new Map(people.map((person) => [person.uuid, person]));
  const classGridCardsByLesson = groupBy(gridCards.classGridCards, (card) => card.lessonUuid);
  const teacherGridCardsByLesson = groupBy(gridCards.teacherGridCards, (card) => card.lessonUuid);
  const roomGridCardsByLesson = groupBy(gridCards.roomGridCards, (card) => card.lessonUuid);

  return cards.map((card): TeachingBlockCardType => {
    const lesson = lessonByUuid.get(card.lessonUuid);
    const subject = lesson ? subjectByUuid.get(lesson.subject.uuid) : undefined;

    const normalizedStartDate = card.startDate ? dayjs(card.startDate).startOf('day').toDate() : null;
    const normalizedEndDate = card.endDate ? dayjs(card.endDate).startOf('day').toDate() : null;
    const start = dayjs(normalizedStartDate).startOf('day');
    const end = dayjs(normalizedEndDate).startOf('day');
    const difference =
      normalizedStartDate && normalizedEndDate ? getDaysDifference(start, end, true) : (card.duration ?? 1) * 7;

    // grid cards (alle Grid-Karten der Zuordnung, wie bisher)
    const classGridCards = classGridCardsByLesson.get(card.lessonUuid) ?? [];
    const teacherGridCards = teacherGridCardsByLesson.get(card.lessonUuid) ?? [];
    const roomGridCards = roomGridCardsByLesson.get(card.lessonUuid) ?? [];

    // data
    const lessonClasses = lesson?.lessonClasses ?? [];
    const groupUuids = new Set(lessonClasses.flatMap((lc) => lc.groups.map((g) => g.uuid)));
    const cardGroups = groups.filter((group) => groupUuids.has(group.uuid));

    const cardClasses = lessonClasses
      .map((lc) => classByUuid.get(lc.class.uuid))
      .filter((c): c is Class => !!c)
      .sort((a, b) => a.name.localeCompare(b.name));

    const teachers = lesson?.teachers.map((t) => personByUuid.get(t.uuid) ?? ({} as Person)) ?? [];

    // positions + divisions
    const positions: number[] = [];
    classGridCards.forEach((c) => {
      if ((c.divisionsCount ?? 1) > 1) {
        positions.push(c.usedPosition ?? 1);
      } else {
        positions.push(0);
      }
    });

    const biggestDivisionsCount = Math.max(...classGridCards.map((c) => c.divisionsCount ?? 1));
    const cardWithBiggestDivisionsCount = classGridCards.find((c) => c.divisionsCount === biggestDivisionsCount);

    const rows: BadgeCardRow[] = createIntArray(cardWithBiggestDivisionsCount?.divisionsCount ?? 1).map((i) => {
      if (positions.includes(i)) {
        return {
          rowColor: '',
          colColors: cardWithBiggestDivisionsCount?.colors ?? [],
        };
      }
      return {
        rowColor: '',
        colColors: [],
      };
    });

    return {
      uuid: card.uuid,
      endDate: normalizedEndDate,
      startDate: normalizedStartDate,
      locked: card.locked ?? false,
      includeHoliday: card.includeHoliday ?? false,
      duration: card.duration ?? null,
      label: subject?.name ?? '',
      labelShort: subject?.shortName ?? '',
      durationInDays: difference ?? 0,
      rooms: card.roomUuids.map((uuid) => ({ uuid })),
      subject,
      lesson: lesson
        ? {
            uuid: lesson.uuid,
            elective: lesson.elective,
            roomSupplyConnection: {
              edges: [...lesson.roomSupply].sort((a, b) => a.order - b.order).map(({ uuid }) => ({ node: { uuid } })),
            },
          }
        : undefined,
      classGridCards,
      teacherGridCards,
      roomGridCards,
      lessonClasses,
      classes: cardClasses,
      teachers,
      groups: cardGroups,
      groupClassNames: lesson?.groupClassNames ?? '',
      badgeCardRows: rows,
      badgeCardTextColor: cardWithBiggestDivisionsCount?.textColor ?? '',
    };
  });
}

/**
 * Kennung dessen, woraus die Karten des Boards bestehen – ohne ihre Position. Ändert sie sich, sind
 * Karten dazugekommen oder weggefallen oder eine Zuordnung hat andere Klassen, Lehrkräfte oder Räume
 * bekommen (z. B. über die Zuordnungs-Tabelle der Version). Der einmal gebaute Board-Store kennt
 * solche Änderungen nicht und muss dann neu aufgebaut werden. Stecken, Verschieben, Sperren und
 * Raum-Zuweisung im Board ändern die Kennung nicht.
 */
export const boardCardsSignature = (
  cards: Array<Pick<TeachingBlockVersionDataCard, 'uuid' | 'lessonUuid' | 'duration'>>,
  lessons: Array<
    Pick<LessonItemFragment, 'uuid' | 'elective' | 'subject' | 'teachers' | 'lessonClasses' | 'roomSupply'>
  >,
): string => {
  const usedLessons = new Set(cards.map((card) => card.lessonUuid));
  const cardPart = cards
    .map((card) => `${card.uuid}:${card.lessonUuid}:${card.duration ?? ''}`)
    .sort()
    .join('|');
  const lessonPart = lessons
    .filter((lesson) => usedLessons.has(lesson.uuid))
    .map((lesson) =>
      [
        lesson.uuid,
        lesson.subject.uuid,
        lesson.elective ? 1 : 0,
        lesson.teachers.map((teacher) => teacher.uuid).join(','),
        lesson.lessonClasses
          .map((lc) => `${lc.class.uuid}/${lc.usedDivision?.uuid ?? ''}/${lc.groups.map((g) => g.uuid).join('+')}`)
          .join(','),
        lesson.roomSupply.map((room) => room.uuid).join(','),
      ].join(':'),
    )
    .sort()
    .join('|');
  return `${cardPart}#${lessonPart}`;
};
